// Shared with mju-ai-workbench/shared/schemas/backupV2.ts — keep the two identical.
/**
 * **Encrypted backups, version 2** (docs/research/2026-10-saas-tiers.md, "Backup and restore";
 * the client's half is docs/workbench-integration.md section 11). The same contract on a
 * self-hosted Enterprise platform and on Mojo Up AI Cloud. Schema version {@link BACKUP_V2_VERSION}.
 *
 * The workbench encrypts everything before it leaves the machine. The platform keeps only
 * ciphertext it cannot read, the plaintext list of which encrypted objects make up each
 * snapshot (so it can count references, apply retention and collect garbage without a key),
 * and the data key wrapped once per **recipient**. It hands out short-lived presigned URLs to
 * the storage service, confined to `tenant/<tenantId>/user/<userId>/`, so the bytes never pass
 * through the API.
 *
 * Words for people: back up, restore, a backup (a snapshot), who can unlock your backups
 * (recipients), your recovery key ("Emergency Kit"), the organisation recovery key.
 *
 * Routes (AIOE). Each is the PERSON's own: a sign-in or that person's desktop token, never a
 * node's; the organisation comes from the token (and `X-Aioe-Organisation` on Cloud), never
 * the body. A platform with no backup storage configured answers 503 { error: "backups_off" }.
 *
 *   GET    /backups/v2/repository                 → 200 BackupRepositoryAnswer (repository null until created)
 *   PUT    /backups/v2/repository                 BackupRepositoryChange → 200/201 BackupRepositoryAnswer
 *            create: 409 when one exists; add a recipient: the caller supplies the data key
 *            already wrapped for it (the platform never sees a key); 409 stale_revision when
 *            `ifRevision` is not current; 422 organisation_recipient_required when policy
 *            requires the organisation recipient and the change would leave it out
 *   POST   /backups/v2/chunks/missing             BackupMissingRequest → 200 BackupMissingAnswer
 *            413 quota_exceeded (nothing reserved) when the new bytes would pass the quota
 *   POST   /backups/v2/chunks/uploaded            { ids } → 200 { stored, missing }: optional, confirms
 *            uploads as they finish so the commit has less to check
 *   POST   /backups/v2/snapshots                  BackupSnapshotCommit → 201 BackupSnapshotSummary
 *            409 chunks_missing { missing } when an object is not in storage (upload it, commit again)
 *            409 no_repository · 422 organisation_recipient_required
 *   GET    /backups/v2/snapshots[?deleted=1]      → 200 { snapshots: BackupSnapshotSummary[] } newest first
 *   GET    /backups/v2/snapshots/:id[?cursor=&limit=]  → 200 BackupSnapshotDownload (paged chunk URLs;
 *            the first page is audited as a restore download)
 *   DELETE /backups/v2/snapshots/:id              → 204, kept for {@link BACKUP_SOFT_DELETE_DAYS} days first
 *   POST   /backups/v2/snapshots/:id/undelete     → 200 BackupSnapshotSummary while still kept
 *
 * Administrators (owners and admins), from the console or a workbench:
 *
 *   GET    /admin/v1/backups                      → 200 BackupAdminOverview (summaries only)
 *   DELETE /admin/v1/backups/:userId              → 204: every snapshot of that person (soft)
 *   DELETE /admin/v1/backups/:userId/snapshots/:id → 204 (soft)
 *   GET    /admin/v1/backups/recovery-key         → 200 { recoveryKey: OrganisationRecoveryKey | null }
 *   PUT    /admin/v1/backups/recovery-key         OrganisationRecoveryKeyPut → 200 { recoveryKey }
 *   GET    /admin/v1/backups/:userId/recovery     → 200 BackupRecoveryAnswer: the person's data key
 *            wrapped for the organisation recipient (audited); 404 when it has none
 *   PUT    /admin/v1/backups/:userId/repository   { op: "addRecipient" | "removeRecipient", … } as above,
 *            for organisation recovery (audited)
 *
 * Rules both sides keep:
 *
 * - **The key never travels.** A recipient carries `wrappedKey`, the repository's data key
 *   sealed to that recipient's public key or secret; the platform stores and returns it as it
 *   came and refuses any body field it does not know (so a raw key cannot be sent by mistake).
 * - **Content addressing.** An object's id is HMAC-SHA256 under a key derived from the data key
 *   ({@link backupObjectIdSchema}), so the platform cannot test for a known file and two
 *   people's identical files share nothing.
 * - **Every object is checked** before a snapshot is committed: listed in a missing answer and
 *   present in storage with the size declared.
 * - **Retention** is the plan's ({@link backupRetentionSchema}): the newest snapshot is always
 *   kept; others outside the policy are deleted softly by the platform's sweep, then purged
 *   after {@link BACKUP_SOFT_DELETE_DAYS} days, and an object no remaining snapshot needs is
 *   removed from storage.
 */
import { z } from "zod";
export const BACKUP_V2_VERSION = 2;
export const BACKUP_V2_PREFIX = "/backups/v2";
/** The largest object (a chunk or a manifest) a presigned PUT accepts. */
export const MAX_BACKUP_OBJECT_BYTES = 16 * 1024 * 1024;
/** Objects one missing request may ask about. */
export const MAX_BACKUP_MISSING_BATCH = 2000;
/** Objects one snapshot may reference (about 200 GiB at a 1 MiB average). */
export const MAX_SNAPSHOT_CHUNKS = 200_000;
/** Chunk URLs per page of a snapshot download. */
export const MAX_BACKUP_DOWNLOAD_PAGE = 1000;
/** Seconds a presigned URL lives. */
export const BACKUP_URL_TTL_SECONDS = 15 * 60;
/** Days a deleted snapshot is kept, restorable with undelete, before it is purged. */
export const BACKUP_SOFT_DELETE_DAYS = 30;
/** Recipients one repository may hold. */
export const MAX_BACKUP_RECIPIENTS = 50;
/** A chunk or manifest id: HMAC-SHA256 (lower-case hex, 64 characters). */
export const backupObjectIdSchema = z.string().regex(/^[0-9a-f]{64}$/, "64 lower-case hex characters (HMAC-SHA256)");
const base64 = (max) => z.string().min(1).max(max).regex(/^[A-Za-z0-9+/_-]+={0,2}$/, "base64 or base64url");
const label = z.string().max(200);
/**
 * Who can unlock a repository: the data key wrapped once for each.
 * - `device`: an enrolled desktop's key pair, private half in the OS keychain. The everyday unlock.
 * - `recovery`: the person's recovery key, shown once in an Emergency Kit.
 * - `organisation`: the organisation recovery key ({@link organisationRecoveryKeySchema}).
 * - `escrow`: the platform's key service, when it offers one (Cloud Free "Mojo Up can help me
 *   recover"; a customer's own key service on Enterprise). Not end to end; off by default.
 */
export const BACKUP_RECIPIENT_KINDS = ["device", "recovery", "organisation", "escrow"];
export const backupRecipientInputSchema = z
    .object({
    /** Stable and unique in the repository: `device:<deviceId>`, `recovery:<fingerprint>`, the organisation key's id. */
    recipientId: z.string().min(1).max(200).regex(/^[A-Za-z0-9._:@-]+$/, "letters, digits and . _ : @ -"),
    kind: z.enum(BACKUP_RECIPIENT_KINDS),
    /** The recipient's public key (base64), when it has one; a recovery key may be symmetric. */
    publicKey: base64(2000).optional(),
    /** The repository data key sealed for this recipient (base64). Opaque to the platform. */
    wrappedKey: base64(4096),
    /** How it was wrapped, e.g. `X25519-HKDF-SHA256+XChaCha20-Poly1305` or `Argon2id+XChaCha20-Poly1305`. */
    alg: z.string().min(1).max(100),
    /** What a person reads: the device's name, "Emergency Kit made 4 Oct 2026". */
    label: label.optional(),
})
    .strict();
export const backupRecipientSchema = backupRecipientInputSchema.extend({
    addedAt: z.string().max(40),
    /** The person who added it (the owner, or an administrator during organisation recovery). */
    addedBy: z.string().max(200).optional(),
});
/** How the client cuts, compresses and seals: stored as given so every device of the person reads it the same way. */
export const backupFormatSchema = z
    .object({
    chunker: z.literal("fastcdc-2020").default("fastcdc-2020"),
    minSize: z.number().int().min(64 * 1024).max(MAX_BACKUP_OBJECT_BYTES).default(512 * 1024),
    avgSize: z.number().int().min(256 * 1024).max(MAX_BACKUP_OBJECT_BYTES).default(1024 * 1024),
    maxSize: z.number().int().min(512 * 1024).max(MAX_BACKUP_OBJECT_BYTES - 64 * 1024).default(8 * 1024 * 1024),
    compression: z.enum(["zstd", "none"]).default("zstd"),
    aead: z.literal("xchacha20-poly1305").default("xchacha20-poly1305"),
    objectId: z.literal("hmac-sha256").default("hmac-sha256"),
    /** The salt the client derives the id and encryption subkeys with (base64, random per repository). */
    salt: base64(200),
})
    .strict();
export const backupV2RepositorySchema = z.object({
    version: z.literal(BACKUP_V2_VERSION),
    /** `bkr_…`, chosen by the platform. */
    id: z.string().max(100),
    format: backupFormatSchema,
    recipients: z.array(backupRecipientSchema).max(MAX_BACKUP_RECIPIENTS),
    /** Goes up by one with every change; send it back as `ifRevision`. */
    revision: z.number().int().positive(),
    createdAt: z.string().max(40),
    updatedAt: z.string().max(40),
});
/** PUT /backups/v2/repository */
export const backupRepositoryChangeSchema = z.discriminatedUnion("op", [
    z.object({ op: z.literal("create"), format: backupFormatSchema, recipients: z.array(backupRecipientInputSchema).min(1).max(MAX_BACKUP_RECIPIENTS) }).strict(),
    z.object({ op: z.literal("addRecipient"), recipient: backupRecipientInputSchema, ifRevision: z.number().int().positive().optional() }).strict(),
    z.object({ op: z.literal("removeRecipient"), recipientId: z.string().min(1).max(200), ifRevision: z.number().int().positive().optional() }).strict(),
]);
/**
 * Grandfather-father-son: keep the newest snapshot in each of the last `hourly` hours, `daily`
 * days, `weekly` ISO weeks and `monthly` months (UTC, by when the platform committed it), and
 * every snapshot younger than `withinDays`. The newest snapshot is always kept.
 */
export const backupRetentionSchema = z.object({
    hourly: z.number().int().nonnegative(),
    daily: z.number().int().nonnegative(),
    weekly: z.number().int().nonnegative(),
    monthly: z.number().int().nonnegative(),
    withinDays: z.number().int().nonnegative(),
});
export const BACKUP_PLANS = ["free", "teams", "enterprise"];
export const backupQuotaSchema = z.object({
    /** Bytes this person's objects take in this organisation (chunks and manifests, counted once). */
    usedBytes: z.number().int().nonnegative(),
    /** Null: no limit. */
    limitBytes: z.number().int().positive().nullable(),
});
/** The organisation recipient as a member's workbench needs it: wrap the data key to `publicKey`. */
export const organisationRecipientSchema = z.object({
    recipientId: z.string().max(200),
    publicKey: z.string().max(2000),
    alg: z.string().max(100),
    /** Policy: every repository must include this recipient (snapshots are refused until it does). */
    required: z.boolean(),
});
/** GET and PUT /backups/v2/repository */
export const backupRepositoryAnswerSchema = z.object({
    version: z.literal(BACKUP_V2_VERSION),
    repository: backupV2RepositorySchema.nullable(),
    plan: z.enum(BACKUP_PLANS),
    quota: backupQuotaSchema,
    retention: backupRetentionSchema,
    /** Set when the organisation has a recovery key. */
    organisation: organisationRecipientSchema.optional(),
    /** Set when the platform offers a key service to escrow with (`kind: "escrow"`). */
    escrow: z.object({ recipientId: z.string().max(200), publicKey: z.string().max(2000), alg: z.string().max(100), label: label }).optional(),
});
/**
 * An object about to be uploaded: its id, its exact encrypted size, and the SHA-256 of its
 * encrypted bytes (standard base64). Storage that can check the digest is told to (S3 binds it
 * into the signature, which Object Lock also needs), so a damaged upload is refused there.
 */
export const backupObjectSizeSchema = z
    .object({
    id: backupObjectIdSchema,
    size: z.number().int().positive().max(MAX_BACKUP_OBJECT_BYTES),
    sha256: z.string().regex(/^[A-Za-z0-9+/]{43}=$/, "the SHA-256 of the encrypted bytes, standard base64"),
})
    .strict();
/** POST /backups/v2/chunks/missing: the objects about to be referenced, with their encrypted sizes. */
export const backupMissingRequestSchema = z
    .object({
    chunks: z.array(backupObjectSizeSchema).max(MAX_BACKUP_MISSING_BATCH),
    /** The snapshot's encrypted manifest, uploaded like a chunk before the commit. */
    manifest: backupObjectSizeSchema.optional(),
})
    .strict();
/** A request the client makes to storage as given: method, URL and these headers (and nothing that would change the signature). */
export const presignedRequestSchema = z.object({
    method: z.enum(["PUT", "GET"]),
    url: z.string(),
    headers: z.record(z.string(), z.string()),
});
export const backupMissingAnswerSchema = z.object({
    /** What to upload: one presigned PUT per object the platform does not have. */
    missing: z.array(z.object({ id: backupObjectIdSchema, upload: presignedRequestSchema })),
    manifest: z.object({ id: backupObjectIdSchema, upload: presignedRequestSchema }).optional(),
    /** How many of the asked-for chunks it already has. */
    present: z.number().int().nonnegative(),
    expiresAt: z.string().max(40),
    quota: backupQuotaSchema,
});
/** POST /backups/v2/snapshots */
export const backupSnapshotCommitSchema = z
    .object({
    manifestId: backupObjectIdSchema,
    /** Every chunk the snapshot needs (order does not matter; repeats are counted once). */
    chunkIds: z.array(backupObjectIdSchema).max(MAX_SNAPSHOT_CHUNKS),
    sizes: z.object({ original: z.number().int().nonnegative(), files: z.number().int().nonnegative().optional() }).strict(),
    /** Taken from the token for a desktop (and must match when given); required from a sign-in. */
    deviceId: z.string().min(1).max(200).optional(),
    deviceName: label.optional(),
    appVersion: z.string().min(1).max(50),
    /** When the client took it (ISO); retention goes by when the platform committed it. */
    createdAt: z.string().max(40),
    /** `scheduled`, `manual`, `before-restore`: what a person reads beside it. */
    reason: z.enum(["scheduled", "manual", "before-restore"]).default("scheduled"),
})
    .strict();
export const backupSnapshotSummarySchema = z.object({
    id: z.string().max(100),
    deviceId: z.string().max(200),
    deviceName: z.string().max(200).optional(),
    appVersion: z.string().max(50),
    reason: z.enum(["scheduled", "manual", "before-restore"]),
    createdAt: z.string().max(40),
    committedAt: z.string().max(40),
    manifestId: backupObjectIdSchema,
    chunkCount: z.number().int().nonnegative(),
    /** Bytes the snapshot's objects take in storage (encrypted, compressed, counted once). */
    storedBytes: z.number().int().nonnegative(),
    /** Bytes it added: objects no earlier snapshot had. */
    newBytes: z.number().int().nonnegative(),
    originalBytes: z.number().int().nonnegative(),
    files: z.number().int().nonnegative().optional(),
    /** Set once deleted (by its person, an administrator or retention); purged at `purgeAfter`. */
    deletedAt: z.string().max(40).optional(),
    deletedBy: z.enum(["owner", "administrator", "retention"]).optional(),
    purgeAfter: z.string().max(40).optional(),
});
/** GET /backups/v2/snapshots/:id: the manifest and one page of chunks, each with a presigned GET. */
export const backupSnapshotDownloadSchema = z.object({
    snapshot: backupSnapshotSummarySchema,
    manifest: z.object({ id: backupObjectIdSchema, size: z.number().int().nonnegative(), download: presignedRequestSchema }),
    chunks: z.array(z.object({ id: backupObjectIdSchema, size: z.number().int().nonnegative(), download: presignedRequestSchema })),
    /** Pass as `cursor` for the next page; absent on the last. */
    next: z.string().max(100).optional(),
    expiresAt: z.string().max(40),
});
/** The organisation recovery key: its public half, and its private half wrapped once per owner (opaque). */
export const organisationRecoveryKeyHolderSchema = z
    .object({
    /** The owner or administrator who can unwrap this copy. */
    userId: z.string().min(1).max(200),
    /** Which of their keys it is wrapped to (a device of theirs, their own recovery key). */
    keyId: z.string().min(1).max(200),
    publicKey: base64(2000).optional(),
    wrappedPrivateKey: base64(8192),
    alg: z.string().min(1).max(100),
})
    .strict();
export const organisationRecoveryKeySchema = z.object({
    recipientId: z.string().max(200),
    publicKey: z.string().max(2000),
    alg: z.string().max(100),
    required: z.boolean(),
    holders: z.array(organisationRecoveryKeyHolderSchema.extend({ name: z.string().max(200).optional() })),
    revision: z.number().int().positive(),
    createdAt: z.string().max(40),
    updatedAt: z.string().max(40),
    updatedBy: z.string().max(200),
});
/** PUT /admin/v1/backups/recovery-key: set (or replace) the key, or change only whether it is required. */
export const organisationRecoveryKeyPutSchema = z.union([
    z
        .object({
        recipientId: z.string().min(1).max(200).regex(/^[A-Za-z0-9._:@-]+$/),
        publicKey: base64(2000),
        alg: z.string().min(1).max(100),
        holders: z.array(organisationRecoveryKeyHolderSchema).min(1).max(50),
        required: z.boolean().default(false),
    })
        .strict(),
    z.object({ required: z.boolean() }).strict(),
]);
/** GET /admin/v1/backups/:userId/recovery */
export const backupRecoveryAnswerSchema = z.object({
    userId: z.string(),
    repositoryId: z.string(),
    revision: z.number().int().positive(),
    format: backupFormatSchema,
    recipient: backupRecipientSchema,
});
export const backupPersonSummarySchema = z.object({
    userId: z.string(),
    name: z.string().optional(),
    email: z.string().optional(),
    snapshots: z.number().int().nonnegative(),
    devices: z.number().int().nonnegative(),
    usedBytes: z.number().int().nonnegative(),
    lastBackupAt: z.string().optional(),
    recipients: z.number().int().nonnegative(),
    hasOrganisationRecipient: z.boolean(),
});
/** GET /admin/v1/backups */
export const backupAdminOverviewSchema = z.object({
    plan: z.enum(BACKUP_PLANS),
    quotaPerPerson: z.number().int().positive().nullable(),
    retention: backupRetentionSchema,
    recoveryKey: z.object({ set: z.boolean(), required: z.boolean(), holders: z.number().int().nonnegative() }),
    people: z.array(backupPersonSummarySchema),
    /** With `limit`, `q` or a filter asked for: where the next page of people starts (additive: absent when there is no more). */
    next: z.string().optional(),
    /** The organisation's people counted, whatever page `people` is (additive; always sent). */
    totals: z.object({ people: z.number().int().nonnegative(), withBackups: z.number().int().nonnegative(), snapshots: z.number().int().nonnegative(), usedBytes: z.number().int().nonnegative(), covered: z.number().int().nonnegative(), stale: z.number().int().nonnegative(), overQuota: z.number().int().nonnegative() }).optional(),
});
/**
 * Which snapshots a retention policy keeps, by id. `snapshots` need not be sorted. Shared so a
 * workbench can show "kept until" the same way the platform decides it.
 */
export function retainedSnapshots(snapshots, policy, now) {
    const sorted = [...snapshots].sort((a, b) => b.at.localeCompare(a.at));
    const keep = new Set();
    if (sorted[0])
        keep.add(sorted[0].id);
    const within = now.getTime() - policy.withinDays * 86_400_000;
    for (const s of sorted)
        if (policy.withinDays > 0 && Date.parse(s.at) >= within)
            keep.add(s.id);
    const buckets = [
        [policy.hourly, (d) => d.toISOString().slice(0, 13)],
        [policy.daily, (d) => d.toISOString().slice(0, 10)],
        [policy.weekly, isoWeek],
        [policy.monthly, (d) => d.toISOString().slice(0, 7)],
    ];
    for (const [count, key] of buckets) {
        if (count <= 0)
            continue;
        const seen = new Set();
        for (const s of sorted) {
            const k = key(new Date(s.at));
            if (seen.has(k))
                continue;
            seen.add(k);
            keep.add(s.id);
            if (seen.size >= count)
                break;
        }
    }
    return keep;
}
/** `2026-W40`: the ISO-8601 week a UTC date falls in. */
export function isoWeek(d) {
    const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
    const week = Math.ceil(((t.getTime() - yearStart) / 86_400_000 + 1) / 7);
    return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}
//# sourceMappingURL=backupV2.js.map